import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form } from "antd"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { modelSelector } from "store/selectors"
import { useSelector } from "react-redux"
import { TimezoneSelect } from "./TimezoneSelect"
import { DaySelector } from "./DaySelector"
import { TimeBlockForm } from "./TimeBlockForm"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"

export const DaysAndHours = ({ form }) => {
  const [t] = useTranslation()
  const trip = useSelector(modelSelector("trip"))
  const availableHours = useSelector((state) => state.trips.trip.available_hours)

  const [selectedTimezone, setSelectedTimezone] = useState(null)
  const [selectedDays, setSelectedDays] = useState([])

  const handleTimezoneChange = (value) => {
    setSelectedTimezone(value)
    form.setFieldsValue({ time_zone: value })
  }

  useEffect(() => {
    const data = Object.keys(trip?.available_days).filter(day => trip?.available_days[day])
    setSelectedDays(data)
  }, [trip?.available_days])

  useEffect(() => {
    if (availableHours?.length > 0) {
      const converted = availableHours.map((block) => ({
        start: block.start ? dayjs(block.start, "hh:mm") : null,
        end: block.end ? dayjs(block.end, "hh:mm") : null,
      }));
      form.setFieldsValue({ available_hours: converted });
    } else {
      form.setFieldsValue({ available_hours: [{ start: null, end: null }] });
    }
  }, [availableHours, form]);

  useEffect(() => {
    const availableDays = form.getFieldValue('available_days');
    let hasAvailableDays = false;
    let hasSelectedDays = selectedDays && selectedDays.length > 0;

    // Check availableDays (can be array or object)
    if (Array.isArray(availableDays)) {
      hasAvailableDays = availableDays.length > 0;
    } else if (availableDays && typeof availableDays === 'object') {
      hasAvailableDays = Object.values(availableDays).some(Boolean);
    }
    // Only show error if both availableDays and selectedDays are empty
    if (!hasAvailableDays && !hasSelectedDays) {
      form.setFields([{
        name: 'available_days',
        errors: [t("experiences.scheduling.errors.day_and_time")]
      }]);
    } else {
      form.setFields([{
        name: 'available_days',
        errors: []
      }]);
    }
  }, [form, selectedDays, t]);

  return (
    <Card
      title={
        <div className="align-center gap-xs">
          {t("experiences.scheduling.sections.days_and_hours.title")}
          <InfoTooltip title={t("experiences.scheduling.sections.days_and_hours.tooltip")} />
        </div>
      }
      className="mb-3xl"
    >
      <Form.Item name="time_zone" label={t("experiences.scheduling.labels.timezone")} rules={[{ required: true }]}>
        <TimezoneSelect className="mb-l" onChange={handleTimezoneChange} value={selectedTimezone} />
      </Form.Item>
      <Text className="block mb-s" type="subtitle" fw={500}>
        {t("experiences.scheduling.labels.day_and_time")}
      </Text>
      <Form.Item
        name="available_days"
        className="mb-xxl"
      >
        <DaySelector
          form={form}
          selectedDays={selectedDays}
          setSelectedDays={setSelectedDays}
          asObject={true}
        />
      </Form.Item>
      {selectedDays.length > 0 && <TimeBlockForm day={selectedDays[0]} form={form} />}
    </Card>
  )
}

DaysAndHours.propTypes = {
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func,
    setFields: PropTypes.func,
  }).isRequired,
}